import { List, ListItem, ListItemText, styled, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';

import { api } from '../../../lib/api';
import { Section } from '../../../components/Section';
import { Query } from '../../../components/Query';
import { Button } from '../../../components/Button';
import {
    formationWithOptionSlugType,
    selectedTrainingType,
    TRAINING_WITH_OPTIONS_SLUGS,
    TRAINING_WITHOUT_OPTIONS_SLUGS,
} from '../../types';
import { formationContinuePricesHandler } from '../lib/formationContinuePricesHandler';
import {
    formationWithOptionsMapping,
    formationWithoutOptionsMapping,
    fundingMapping,
} from '../../constants';
import { formationContinueEntryType } from '../../../lib/api/api';
import { convertToFrenchDate } from '../../../lib/utils';

function FormationContinueEntryForm(props: { id: string }) {
    return (
        <MainContainer>
            <HeaderContainer className="not-printable">
                <Button
                    fullWidth
                    startIcon={<PrintIcon />}
                    variant="contained"
                    onClick={handlePrint}
                >
                    Imprimer
                </Button>
            </HeaderContainer>
            <Query
                queryKey={['formation-continue-entries', props.id]}
                apiCall={() => api.getFormationContinue(props.id)}
            >
                {(formationContinueEntry) => {
                    const { selectedTrainings, discount, totalPrice } =
                        computeTrainingData(formationContinueEntry);

                    return (
                        <>
                            <Section variant="h2" title="Informations personnelles">
                                <Row>
                                    <LabelText>Nom :</LabelText>
                                    <ValueText>
                                        {formationContinueEntry.firstName}{' '}
                                        {formationContinueEntry.lastName}
                                    </ValueText>
                                </Row>

                                <Row>
                                    <LabelText>Date de naissance :</LabelText>
                                    <ValueText>{formationContinueEntry.birthdate}</ValueText>
                                </Row>
                                <Row>
                                    <LabelText>Nationalité / Lieu de naissance : </LabelText>
                                    <ValueText>{formationContinueEntry.nationality}</ValueText>
                                </Row>
                                <Row>
                                    <LabelText>Contact : </LabelText>
                                    <ValueText>
                                        {formationContinueEntry.email} -{' '}
                                        {formationContinueEntry.phoneNumber}
                                    </ValueText>
                                </Row>
                                <Row>
                                    <LabelText>Adresse : </LabelText>
                                    <ValueText>{formationContinueEntry.address}</ValueText>
                                </Row>
                            </Section>
                            <Section variant="h2" title="Parcours étudiant et professionnel">
                                <Section variant="h3" title="Diplômes obtenus">
                                    {formationContinueEntry.diplomas.length > 0 ? (
                                        <List>
                                            {formationContinueEntry.diplomas.map(
                                                (diploma, index) => (
                                                    <ListItem key={index}>
                                                        <ListItemText primary={diploma} />
                                                    </ListItem>
                                                ),
                                            )}
                                        </List>
                                    ) : (
                                        <Typography>Aucun diplôme renseigné</Typography>
                                    )}
                                </Section>
                                <Section variant="h3" title="Derniers postes occupés">
                                    {formationContinueEntry.jobs.length > 0 ? (
                                        <List>
                                            {formationContinueEntry.jobs.map((job, index) => (
                                                <ListItem key={index}>
                                                    <ListItemText primary={job} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : (
                                        <Typography>Aucun poste renseigné</Typography>
                                    )}
                                </Section>
                            </Section>
                            <Section variant="h2" title="Formation(s) choisie(s)">
                                {selectedTrainings.length > 0 ? (
                                    <List>
                                        {selectedTrainings.map((training, index) => (
                                            <ListItem key={index}>
                                                <Typography>{training}</Typography>
                                            </ListItem>
                                        ))}
                                    </List>
                                ) : (
                                    <Typography>Aucune formation choisie</Typography>
                                )}
                            </Section>
                            <Section variant="h2" title="Financement">
                                <Typography variant="h4">
                                    {fundingMapping[formationContinueEntry.fundingKind].label}
                                </Typography>
                                {formationContinueEntry.fundingCompany && (
                                    <Row>
                                        <LabelText>Nom de l'entreprise :</LabelText>
                                        <ValueText>
                                            {formationContinueEntry.fundingCompany}
                                        </ValueText>
                                    </Row>
                                )}
                                {formationContinueEntry.fundingSiret && (
                                    <Row>
                                        <LabelText>N° de SIRET :</LabelText>
                                        <ValueText>{formationContinueEntry.fundingSiret}</ValueText>
                                    </Row>
                                )}
                                {formationContinueEntry.fundingAddress && (
                                    <Row>
                                        <LabelText>Adresse de l'entreprise :</LabelText>
                                        <ValueText>
                                            {formationContinueEntry.fundingAddress}
                                        </ValueText>
                                    </Row>
                                )}
                                {formationContinueEntry.fundingManagementName && (
                                    <Row>
                                        <LabelText>
                                            Prénom et NOM du/de la référent·e (direction) :
                                        </LabelText>
                                        <ValueText>
                                            {formationContinueEntry.fundingManagementName}
                                        </ValueText>
                                    </Row>
                                )}
                                {formationContinueEntry.fundingManagementEmail && (
                                    <Row>
                                        <LabelText>Adresse mail de la direction :</LabelText>
                                        <ValueText>
                                            {formationContinueEntry.fundingManagementEmail}
                                        </ValueText>
                                    </Row>
                                )}
                            </Section>
                            <Section variant="h2" title="Récapitulatif">
                                <Typography>
                                    {selectedTrainings.length} formations sélectionnées : 
                                    {totalPrice + discount} €
                                </Typography>
                                {discount > 0 && (
                                    <Typography>Remise appliquée : {discount} €</Typography>
                                )}
                                {discount > 0 && (
                                    <Typography>Coût total : {totalPrice} €</Typography>
                                )}
                            </Section>
                        </>
                    );
                }}
            </Query>
        </MainContainer>
    );

    function handlePrint() {
        window.print();
    }

    function computeTrainingData(formationContinueEntry: formationContinueEntryType) {
        let totalPrice = 0;
        const selectedTrainingsWithOptions: string[] = [];

        for (const TRAINING_WITH_OPTIONS_SLUG of TRAINING_WITH_OPTIONS_SLUGS) {
            const selectedTraining =
                formationContinueEntry.selectedTrainings[TRAINING_WITH_OPTIONS_SLUG];
            if (!selectedTraining) {
                continue;
            }
            const title = formationWithOptionsMapping[TRAINING_WITH_OPTIONS_SLUG].title;
            const selectedDate = computeSelectedDate(TRAINING_WITH_OPTIONS_SLUG, selectedTraining);
            let selectedTrainingWithOptions = `"${title}" (${selectedTraining.trainingOption}) - ${selectedDate}`;
            if (selectedTraining.trainingOption === 'inter') {
                const price = formationContinuePricesHandler.computeFormationWithOptionsPrice(
                    TRAINING_WITH_OPTIONS_SLUG,
                    {
                        fundingKind: formationContinueEntry.fundingKind,
                    },
                );
                totalPrice += price;
                selectedTrainingWithOptions += ` : ${price} €`;
            }
            selectedTrainingsWithOptions.push(selectedTrainingWithOptions);
        }

        const selectedTrainingsWithoutOptions: string[] = [];
        for (const TRAINING_WITHOUT_OPTION_SLUG of TRAINING_WITHOUT_OPTIONS_SLUGS) {
            const selectedTraining =
                formationContinueEntry.selectedTrainings[TRAINING_WITHOUT_OPTION_SLUG];
            if (!selectedTraining) {
                continue;
            }
            const title = formationWithoutOptionsMapping[TRAINING_WITHOUT_OPTION_SLUG].title;
            const readableDate = convertToFrenchDate(selectedTraining.date);

            selectedTrainingsWithoutOptions.push(
                `"${title}" (${selectedTraining.trainingOption}) - ${readableDate}`,
            );
        }

        const selectedTrainings = [
            ...selectedTrainingsWithOptions,
            ...selectedTrainingsWithoutOptions,
        ];

        const selectedMaster = formationContinueEntry.selectedMaster;
        if (selectedMaster !== null) {
            selectedTrainings.push(`Master ${formationContinueEntry.selectedMaster}`);
        }

        const discount = formationContinuePricesHandler.computeDiscountPrice(
            TRAINING_WITH_OPTIONS_SLUGS.filter(
                (slug) => formationContinueEntry.selectedTrainings[slug]?.date !== undefined,
            ),
            { fundingKind: formationContinueEntry.fundingKind },
        );
        if (discount > 0) {
            totalPrice -= discount;
        }

        return { selectedTrainings, discount, totalPrice };
    }

    function computeSelectedDate(
        trainingSlug: formationWithOptionSlugType,
        selectedTraining: selectedTrainingType,
    ) {
        switch (selectedTraining.trainingOption) {
            case 'inter':
                return (
                    formationWithOptionsMapping[trainingSlug].dates.find(
                        (date) => date.value === selectedTraining.date,
                    )?.label || ''
                );
            case 'intra':
                return convertToFrenchDate(selectedTraining.date);
            default:
                return '';
        }
    }
}

const MainContainer = styled('div')(({ theme }) => ({ width: '100%' }));
const Row = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
}));

const ValueText = styled(Typography)(({ theme }) => ({ fontWeight: 'bold' }));
const LabelText = styled(Typography)(({ theme }) => ({ marginRight: theme.spacing(1) }));

const HeaderContainer = styled('div')(({ theme }) => ({
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
}));
export { FormationContinueEntryForm };
